import { Component, Input, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import IPatient from '../../../_interfaces/IPatients';
import { PatientsService } from '../../../_services/patients/patients.service';
import { FormPaymentComponent } from '../../forms/form-payment/form-payment.component';
import IPatientHasTreatment from '../../../_interfaces/IPatientHasTreatment';
import IPayment from '../../../_interfaces/IPayment';
import { FormEditPatientTreatmentComponent } from '../../forms/form-edit-patient-treatment/form-edit-patient-treatment.component';
import { forkJoin, Subscription } from 'rxjs';
import moment from 'moment';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CalendarQuickAppointmentComponent } from '../../../components/calendars/calendar-quick-appointment/calendar-quick-appointment.component';
import { CalendarAppointmentsService } from '../../../_services/calendars/calendar-appointments.service';
import IAppointment from '../../../_interfaces/IAppointment';
import { Router } from '@angular/router';
import { BaseComponent } from '../../../components/base/base.component';
import { SessionService } from '../../../_services/session/session.service';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { TagModule } from 'primeng/tag';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { DeleteButtonTreatmentPatientComponent } from '../../delete-buttons/delete-button-treatment-patient/delete-button-treatment-patient.component';
import { CurrenciesService } from '../../../_services/currencies/currencies.service';

@Component({
  standalone: true,
  selector: 'app-table-treatments',
  templateUrl: './table-patients-treatments.component.html',
  styleUrls: ['./table-patients-treatments.component.scss'],
  imports: [
    TableModule,
    ButtonModule,
    TranslatePipe,

    CommonModule,
    TagModule,
    FormsModule,
    TooltipModule,
    DeleteButtonTreatmentPatientComponent,
  ],
})
export class TablePatientTreatmentsComponent
  extends BaseComponent
  implements OnInit
{
  @Input('treatments') treatments: IPatientHasTreatment[];
  @Input('patient') patient: IPatient;
  @Input('adminReadonly') adminReadonly = false;

  public moment = moment;
  public subscriptionPatientTreatments: Subscription;
  public subscriptionPatientAppointment: Subscription;

  public selectedTreatments: IPatientHasTreatment[] = [];
  private currencySymbolByCode = new Map<string, string>();
  private currencySymbolById = new Map<number, string>();
  constructor(
    public dialogService: DialogService,
    public confirmationService: ConfirmationService,
    public session: SessionService,
    public patientService: PatientsService,
    public messageService: MessageService,
    public errorsService: ErrorsService,
    public translate: TranslateService,
    public calendar: CalendarAppointmentsService,
    public router: Router,
    private currenciesService: CurrenciesService,
  ) {
    super(session);
  }

  public redirectCalendar(appointment: IAppointment) {
    const site = appointment.calendar.site;
    if (this.session.getPermissionOnSite(this.customer, site) != undefined) {
      this.session.setSite(site);
      this.router.navigate(
        [`${this.customer.identifier}/${site.identifier}/appointments`],
        {
          queryParams: {
            date: appointment.start,
          },
        },
      );
    } else {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('MESSAGES.SITES.ERROR.SUMMARY'),
        detail: this.translate.instant('MESSAGES.SITES.ERROR.403'),
      });
    }
  }

  customSort(event: any) {
    if (event.field === 'appointment.start') {
      const direction = event.order;
      this.treatments.sort((data1: any, data2: any) => {
        const value1 = data1.appointment?.start;
        const value2 = data2.appointment?.start;

        if (!value1 && value2) {
          return direction;
        } else if (value1 && !value2) {
          return -direction;
        } else if (!value1 && !value2) {
          return 0;
        }

        return (
          direction * (new Date(value1).getTime() - new Date(value2).getTime())
        );
      });
    }
  }

  public payDialog(treatment: IPatientHasTreatment) {
    if (!this.canManageTreatmentPayments()) return;
    const payment = this.getPrimaryPayment(treatment);
    this.dialogService.open(FormPaymentComponent, {
      closable: true,
      modal: true,
      width: '34rem',
      breakpoints: {
        '960px': '44rem',
        '640px': '96vw',
      },
      header: this.translate.instant(
        payment ? 'PAYMENT.DIALOG.EDIT_TITLE' : 'PAYMENT.DIALOG.TITLE',
        {
          treatment: treatment.treatment.name,
        },
      ),
      inputValues: {
        treatment,
        payload: payment
          ? {
              ...payment,
              id: payment.id,
              treatments_id: treatment.id,
            }
          : undefined,
        onCreate: () => {
          this.loadTreatments();
        },
        onUpdate: () => {
          this.loadTreatments();
        },
        onDelete: () => {
          this.loadTreatments();
        },
      },
    });
  }

  public getPrimaryPayment(treatment: IPatientHasTreatment): IPayment | null {
    if (!treatment?.payments?.length) return null;
    return treatment.payments[0];
  }

  public isPaid(treatment: IPatientHasTreatment): boolean {
    return !!this.getPrimaryPayment(treatment);
  }

  public getPaymentMethodLabel(treatment: IPatientHasTreatment): string {
    const payment = this.getPrimaryPayment(treatment);
    if (!payment) return '';

    const methodName =
      payment.paymentType?.name ||
      (payment as any)?.payment_type?.name ||
      `${payment.payments_types_id || ''}`;

    const key = `PAYMENT_TYPES.${methodName}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : `${methodName}`;
  }

  public getPaymentDateLabel(treatment: IPatientHasTreatment): string {
    const payment = this.getPrimaryPayment(treatment);
    if (!payment?.payment_date) return '';
    return moment(payment.payment_date).format('DD/MM/YYYY');
  }

  public getPaymentSummaryLabel(treatment: IPatientHasTreatment): string {
    const date = this.getPaymentDateLabel(treatment);
    const method = this.getPaymentMethodLabel(treatment);
    if (date && method) return `${date} · ${method}`;
    return date || method;
  }

  public getCurrencyLabel(treatment: IPatientHasTreatment): string {
    const directSymbol = String(treatment?.currency?.symbol || '').trim();
    if (directSymbol) return directSymbol;

    const paymentCurrencyCode = String(
      treatment?.payments?.[0]?.site?.currency || '',
    )
      .trim()
      .toUpperCase();

    const codeCandidates = [
      treatment?.currency?.code,
      paymentCurrencyCode,
      treatment?.appointment?.calendar?.site?.currency,
      this.site?.currency,
      this.customer?.currency,
    ];

    for (const code of codeCandidates) {
      const symbol = this.getCurrencySymbolByCode(code);
      if (symbol) return symbol;
    }

    const customerCurrencyId = Number((this.customer as any)?.currencies_id || 0);
    if (customerCurrencyId > 0) {
      const symbolById = this.currencySymbolById.get(customerCurrencyId);
      if (symbolById) return symbolById;
    }

    const fallbackCode = codeCandidates
      .map((value) => String(value || '').trim().toUpperCase())
      .find(Boolean);
    return fallbackCode || '';
  }

  public getPaymentButtonLabel(treatment: IPatientHasTreatment): string {
    if (this.isPaid(treatment)) {
      return this.translate.instant(
        'APPOINTMENT.PATIENT.TREATMENTS.PAYMENT.EDIT_BUTTON',
      );
    }
    return this.translate.instant('APPOINTMENT.PATIENT.TREATMENTS.MARK.PAID');
  }

  public getPaymentButtonIcon(treatment: IPatientHasTreatment): string {
    return this.isPaid(treatment) ? 'fas fa-pen-to-square' : 'fas fa-money-bill';
  }

  public getPaymentTooltip(treatment: IPatientHasTreatment): string {
    const label = this.getPaymentSummaryLabel(treatment);
    if (label) return label;
    return this.translate.instant('APPOINTMENT.PATIENT.TREATMENTS.MARK.PAID');
  }

  public appointmentStatusColor(treatment: IPatientHasTreatment): string {
    return treatment.appointment?.status?.bgcolor || '';
  }

  public appointmentStatusTextColor(treatment: IPatientHasTreatment): string {
    return treatment.appointment?.status?.textcolor || '';
  }

  public editTreatment(treatment: IPatientHasTreatment) {
    if (!this.canModifyPatientTreatments()) return;
    if (!!!treatment) return;
    this.dialogService.open(FormEditPatientTreatmentComponent, {
      closable: true,
      modal: true,
      width: '42rem',
      breakpoints: {
        '960px': '52rem',
        '640px': '96vw',
      },
      header: this.translate.instant(
        'APPOINTMENT.PATIENT.TREATMENTS.DIALOG.UPDATE.TITLE',
      ),
      data: {
        onEdit: this.loadTreatments.bind(this),
        patient: this.patient,
        treatment,
      },
    });
  }

  public confirmDeleteSelected() {
    if (!this.canModifyPatientTreatments()) return;
    this.confirmationService.confirm({
      header: this.translate.instant(
        'PATIENT.CONFIRMATION.TREATMENT.REMOVE.ALL.TITLE',
      ),
      message: this.translate.instant(
        'PATIENT.CONFIRMATION.TREATMENT.REMOVE.ALL.TEXT',
      ),
      accept: this.removeSelectedTreatments.bind(this),
    });
  }

  public removeSelectedTreatments() {
    if (!this.canModifyPatientTreatments()) return;
    const observables = new Array();
    this.selectedTreatments.forEach((treatment) => {
      observables.push(
        this.patientService.removeTreatment(
          this.customers_id,
          this.patient.id,
          treatment.id,
        ),
      );
    });

    forkJoin(observables).subscribe({
      next: () => {
        this.selectedTreatments = [];
        this.loadTreatments();
        return this.messageService.add({
          severity: 'success',
          detail: this.translate.instant(
            'MESSAGES.TREATMENTS.DELETE.ALL.DETAIL',
          ),
          summary: this.translate.instant(
            'MESSAGES.TREATMENTS.DELETE.ALL.SUMMARY',
          ),
        });
      },
      error: (error) => {
        this.selectedTreatments = [];
        this.loadTreatments();
        const code = this.errorsService.getMessageFromPatientError(error);
        this.messageService.add({
          severity: 'error',
          detail: this.translate.instant(`MESSAGES.PATIENTS.ERROR.${code}`),
          summary: this.translate.instant('MESSAGES.PATIENTS.ERROR.SUMMARY'),
        });
      },
    });
  }

  onDelete = () => {
    this.loadTreatments();
  };

  public scheduleAppointment() {
    if (!this.canScheduleAppointments()) return;
    this.dialogService.open(CalendarQuickAppointmentComponent, {
      closable: true,
      modal: true,
      width: '100%',
      header: 'Quick appointment',
      data: {
        patient: this.patient,
        treatments: this.selectedTreatments,
      },
    });
  }

  public scheduleAppointmentSingle(treatment: IPatientHasTreatment) {
    if (!this.canScheduleAppointments()) return;
    this.dialogService.open(CalendarQuickAppointmentComponent, {
      closable: true,
      modal: true,
      width: '100%',
      header: this.translate.instant('APPOINTMENT.SCHEDULE'),
      data: {
        patient: this.patient,
        treatments: [treatment],
      },
    });
  }

  ngOnDestroy() {
    this.subscriptionPatientAppointment.unsubscribe();
  }

  loadTreatments() {
    const siteId = +this.sites_id || undefined;
    this.patientService
      .getTreatments(this.customers_id, this.patient.id, siteId)
      .subscribe((treatments) => {
        this.treatments = treatments;
      });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadCurrencyCatalog();

    this.subscriptionPatientAppointment = this.calendar
      .listenPatientAppointments$()
      .subscribe(() => {
        this.loadTreatments();
      });
  }

  private loadCurrencyCatalog(): void {
    this.currenciesService.getAll().subscribe({
      next: (currencies) => {
        this.currencySymbolByCode.clear();
        this.currencySymbolById.clear();
        (currencies || []).forEach((currency) => {
          const code = String(currency?.code || '').trim().toUpperCase();
          const symbol = String(currency?.symbol || '').trim();
          if (code && symbol) {
            this.currencySymbolByCode.set(code, symbol);
          }
          const id = Number(currency?.id || 0);
          if (id > 0 && symbol) {
            this.currencySymbolById.set(id, symbol);
          }
        });
      },
    });
  }

  private getCurrencySymbolByCode(code?: string | null): string {
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) return '';
    return this.currencySymbolByCode.get(normalized) || '';
  }

  public canManageTreatmentPayments(): boolean {
    return this.session.canManagePayments(this.customer, this.site);
  }

  public canModifyPatientTreatments(): boolean {
    return this.hasPermission('PATIENTS.UPDATE') && !this.adminReadonly;
  }

  public canScheduleAppointments(): boolean {
    return this.hasPermission('APPOINTMENTS.CREATE') && !this.adminReadonly;
  }
}
